import React, { useEffect, useState } from "react";
import { useAuth } from "../hooks/useAuth";
import "./Profile.css";
import { isFieldVisible } from "../profileTemplates/profileVisibility.utils";
import FieldRenderer from "../profileEngine/FieldRenderer";
import {
  isSignatureValid,
  resolveSignatureName,
} from "../profileEngine/signature.utils";

type Mode = "wizard" | "summary";

const ProfilePage: React.FC = () => {
  const { user, roles } = useAuth();
  const _canReveal = Array.isArray(roles?.permissions)
    ? roles.permissions.includes("profiles.sensitive")
    : false;

  const [loading, setLoading] = useState(true);
  const [template, setTemplate] = useState<any>(null);
  const [savedSubmission, setSavedSubmission] = useState<any>(null);
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [mode, setMode] = useState<Mode>("wizard");
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [profileStatus, setProfileStatus] = useState<string>("draft");
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);

  useEffect(() => {
    if (!user?.userID) {
      setLoading(false);
      return;
    }

    const loadProfile = async () => {
      try {
        const assignRes = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/user-profile-assignments`,
          { credentials: "include" },
        );
        const assignments = await assignRes.json();

        const assignment = assignments.find(
          (a: any) => a.userId === user.userID || a.user_id === user.userID,
        );

        if (!assignment?.profileTemplateId) {
          setTemplate(null);
          setMode("wizard");
          return;
        }

        const templatesRes = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/templates`,
          { credentials: "include" },
        );
        const templates = await templatesRes.json();

        const tpl = templates.find(
          (t: any) =>
            t.id === assignment.profileTemplateId ||
            t.name === assignment.profileTemplateId,
        );

        if (!tpl) return;

        setTemplate(tpl);

        const dataRes = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/profile-submissions/${user.userID}/${tpl.id}`,
          { credentials: "include" },
        );
        const saved = await dataRes.json();

        if (saved?.data) {
          setFormData(saved.data);
          setSavedSubmission(saved);

          const status =
            saved.status || (saved.signatureMeta ? "submitted" : "draft");

          setProfileStatus(status);

          if (status === "submitted") {
            setMode("summary");
          } else {
            setMode("wizard");
          }
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [user]);

  const updateField = (key: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const isEmpty = (v: any) => v === undefined || v === null || v === "";

  const isFieldValid = (field: any) => {
    const value = formData[field.key];

    // Read-only / non-input field types are always satisfied — they carry no
    // user input and must never block submission. (registration stores a JSON
    // string; running it through the length/mask checks below would be wrong.)
    if (field.type === "registration" || field.type === "heading") {
      return true;
    }

    // Same rule the renderer paints the error with — both sides come from
    // signature.utils so the Submit gate can never disagree with the message
    // shown under the input.
    if (field.type === "certification") {
      return isSignatureValid(value, resolveSignatureName(user));
    }

    if (field.required) {
      if (field.type === "checkbox") {
        if (!value) return false;
      } else {
        if (isEmpty(value)) return false;
      }
    }

    if (isEmpty(value)) return true;

    const raw = value.toString();

    if (field.minLength !== undefined && raw.length < field.minLength) {
      return false;
    }

    if (field.maxLength !== undefined && raw.length > field.maxLength) {
      return false;
    }

    if (field.mask) {
      const requiredLength = (field.mask.match(/\*/g) || []).length;
      if (raw.length !== requiredLength) {
        return false;
      }
    }

    return true;
  };

  const isStepValid = (step: any) =>
    (step.fields || [])
      .filter((field: any) => isFieldVisible(field, formData))
      .every(isFieldValid);

  // Locked submissions render against the template snapshot frozen at submit
  // time so later admin edits to the live template don't retroactively
  // remove sections/fields from this user's history. Draft and reopened
  // states keep tracking the live template as before.
  const lockedSchema =
    profileStatus === "submitted"
      ? savedSubmission?.templateSnapshot?.schema
      : null;
  const allSteps = lockedSchema?.steps || template?.schema?.steps || [];

  const publicSteps = allSteps.filter((s: any) => s.scope !== "internal");

  // Union of fieldKeys currently marked sensitive in the LIVE template. If a
  // field was flipped from non-sensitive to sensitive after a prior submit,
  // the templateSnapshot doesn't have the flag — without this we'd render
  // those fields as plain text. Combining snapshot ∪ live makes "once
  // sensitive, always sensitive" the safe default.
  const liveSensitiveKeys = React.useMemo(() => {
    const out = new Set<string>();
    for (const s of template?.schema?.steps || []) {
      for (const f of s.fields || []) {
        if (f && f.sensitive === true && typeof f.key === "string") {
          out.add(f.key);
        }
      }
    }
    return out;
  }, [template]);

  const withSensitiveUnion = (field: any) =>
    field && liveSensitiveKeys.has(field.key) && !field.sensitive
      ? { ...field, sensitive: true }
      : field;

  const step = publicSteps[currentStepIndex];

  useEffect(() => {
    if (currentStepIndex >= publicSteps.length) {
      setCurrentStepIndex(0);
    }
  }, [publicSteps.length, currentStepIndex]);

  const isAllValid = () =>
    publicSteps.every((step: any) =>
      (step.fields || [])
        .filter((field: any) => isFieldVisible(field, formData))
        .every(isFieldValid),
    );
  const saveDraft = async () => {
    if (!template) return;

    await fetch(`${process.env.REACT_APP_BACKEND_URL}/profile-submissions`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        userId: user?.userID,
        profileTemplateId: template.id,
        data: formData,
        status: "in_progress",
      }),
    });
  };

  const submitProfile = async () => {
    if (!template || !isAllValid()) return;

    // Read the signature from the certification field by key. The previous
    // lookup scanned every value in formData for one matching the user's name,
    // so any other field holding the same string (an emergency contact with
    // the same name, a "prepared by" line) could win and land in the signed
    // record instead. Stored verbatim — normalization is for matching only.
    const certificationField = publicSteps
      .flatMap((s: any) => s.fields || [])
      .find(
        (f: any) =>
          f.type === "certification" && isFieldVisible(f, formData),
      );
    const rawSignature = certificationField
      ? formData[certificationField.key]
      : undefined;
    const certificationValue =
      typeof rawSignature === "string" ? rawSignature : "";

    // Check the POST result before transitioning. Previous behaviour
    // silently moved the UI to summary even on backend failure (encryption
    // error, transient Cosmos error, doc-too-large), leaving the on-disk
    // status as "reopened". Users would think submit succeeded, then come
    // back later to find the profile still open.
    const submitRes = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/profile-submissions`,
      {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userId: user.userID,
          profileTemplateId: template.id,
          data: formData,
          status: "submitted",
          signatureMeta: {
            userAgent: navigator.userAgent,
            typedName: certificationValue,
          },
        }),
      },
    );
    if (!submitRes.ok) {
      const body = await submitRes.json().catch(() => ({}));
      const msg =
        body?.error ||
        `Submission failed (${submitRes.status}). Please try again.`;
      alert(msg);
      return;
    }

    // Pull the canonical doc back so the locked summary renders with the
    // server-side signatureMeta.timestamp + templateSnapshot AND so that
    // formData carries the encrypted envelopes (not the plaintext the user
    // typed) — that way the sensitive masking branch in FieldRenderer
    // operates on the at-rest shape instead of in-memory plaintext.
    try {
      const refresh = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/profile-submissions/${user.userID}/${template.id}`,
        { credentials: "include" },
      );
      if (refresh.ok) {
        const saved = await refresh.json();
        if (saved) {
          setSavedSubmission(saved);
          if (saved.data) setFormData(saved.data);
        }
      }
    } catch (_) {
      // Refetch is best-effort. The submit POST already succeeded above,
      // so the disk state is correct. The next mount of ProfilePage will
      // hydrate from disk.
    }

    setProfileStatus("submitted");
    setMode("summary");
  };

  if (loading) {
    return <div style={{ padding: "32px" }}>Loading profile…</div>;
  }

  if (!template) {
    return (
      <div className="profile-page">
        <div className="profile-wizard">
          <p>No profile template assigned.</p>
        </div>
      </div>
    );
  }

  if (mode === "summary") {
    const submittedTimestamp = savedSubmission?.signatureMeta?.timestamp;
    return (
      <div className="profile-page">
        <div className="profile-wizard">
          <h2 className="center-title">Profile</h2>

          {profileStatus === "submitted" && submittedTimestamp && (
            <div
              style={{
                textAlign: "center",
                marginBottom: 16,
                color: "#9aa0a6",
                fontSize: "0.85rem",
              }}
            >
              Submitted on{" "}
              {new Date(submittedTimestamp).toLocaleString()}
            </div>
          )}

          {publicSteps.map((step: any) => (
            <div key={step.id} className="profile-card">
              <div className="card-header">
                <h3>{step.label}</h3>
                {profileStatus !== "submitted" ? (
                  <button
                    className="edit-button"
                    onClick={() => {
                      setMode("wizard");
                      setCurrentStepIndex(publicSteps.indexOf(step));
                    }}
                  >
                    ✏️
                  </button>
                ) : (
                  <span
                    style={{ cursor: "not-allowed", color: "#666" }}
                    title="Profile is Submitted. Contact manager for change request approval."
                  >
                    🔒
                  </span>
                )}
              </div>

              <div className="summary-grid">
                {(step.fields || [])
                  .filter((field: any) => isFieldVisible(field, formData))
                  .map((field: any) => (
                    <FieldRenderer
                      key={field.key}
                      field={withSensitiveUnion(field)}
                      value={formData[field.key]}
                      mode="view"
                      user={user}
                      userId={user?.userID}
                      profileTemplateId={template?.id}
                      /* User-side never gets reveal. Even if the user holds
                         profiles.sensitive, this surface stays masked-only.
                         Reveal is admin-side only and lives in
                         AdminProfileView. */
                      canReveal={false}
                    />
                  ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="profile-page">
      <div className="profile-wizard">
        <div className="profile-steps">
          Step {currentStepIndex + 1} of {publicSteps.length}
        </div>

        <div className="profile-section">
          <h2 className="center-title">{step.label}</h2>

          <div className="two-col-grid">
            {(step.fields || [])
              .filter((field: any) => isFieldVisible(field, formData))
              .map((field: any) => (
                <FieldRenderer
                  key={field.key}
                  field={field}
                  value={formData[field.key]}
                  mode="edit"
                  onChange={(val) => updateField(field.key, val)}
                  user={user}
                />
              ))}
          </div>
        </div>

        <div className="profile-actions right">
          {currentStepIndex > 0 && (
            <button onClick={() => setCurrentStepIndex((i) => i - 1)}>
              Back
            </button>
          )}

          {currentStepIndex < publicSteps.length - 1 ? (
            <button
              className="primary"
              disabled={!isStepValid(step)}
              onClick={async () => {
                await saveDraft();
                setCurrentStepIndex((i) => i + 1);
              }}
            >
              Continue
            </button>
          ) : (
            <button
              className="primary"
              disabled={!isAllValid()}
              onClick={() => setShowSubmitConfirm(true)}
            >
              Submit Profile
            </button>
          )}
        </div>
      </div>
      {showSubmitConfirm && (
        <div className="smrtdocs-modal-overlay">
          <div className="smrtdocs-modal" style={{ width: "480px" }}>
            <div className="smrtdocs-modal-header">
              <h2 style={{ margin: 0, fontSize: "1rem" }}>Submit Profile</h2>
            </div>
            <div className="smrtdocs-modal-body">
              <p style={{ fontSize: "0.9rem", lineHeight: 1.5, margin: "0 0 20px" }}>
                Are you sure you want to submit? You will not be able to edit after submission.
              </p>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <button className="btn-cancel" onClick={() => setShowSubmitConfirm(false)}>Cancel</button>
                <button className="btn-save" onClick={() => { setShowSubmitConfirm(false); submitProfile(); }}>Submit</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfilePage;
